import React, { useState } from "react";
import { useServices } from "../../services/Services";
import { useQueryClient } from "@tanstack/react-query";
import { DYAD_STATUS_PALETTE } from "./dyadConstants";
import { ScoreHistoryChart } from "./ScoreHistoryChart";
import styles from "./AssessmentDetailView.module.css";

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface AssessmentDetailViewProps {
  dyadId?: string;
  assessmentId?: string;
  statusScore?: number;
  trajectory?: string;
  open: boolean;
  children?: React.ReactNode;
}

export const AssessmentDetailView: React.FC<AssessmentDetailViewProps> = ({
  dyadId,
  statusScore,
  trajectory,
  open,
  children,
}) => {
  const services = useServices();
  const queryClient = useQueryClient();
  const { data: dyad, isLoading: isDyadLoading } = services.Dyad.useGetDyadById(
    open ? dyadId : undefined,
  );
  const { data: scoreHistory, isLoading: isHistoryLoading } =
    services.Dyad.useScoreHistory(open ? dyadId : undefined, open);

  const updateMutation = services.Request.useUpdateDyadConfig();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  const handleStartEdit = () => {
    setEditValue(dyad?.companyProfileContext ?? "");
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!dyadId) return;
    await updateMutation.mutateAsync({
      dyadId,
      companyProfileContext: editValue.trim() || undefined,
    });
    queryClient.invalidateQueries({ queryKey: ["dyads", "id", dyadId] });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(dyad?.companyProfileContext ?? "");
    setIsEditing(false);
  };

  const palette = statusScore ? DYAD_STATUS_PALETTE[statusScore] : null;

  return (
    <div className={`${styles.detailWrapper} ${open ? styles.detailOpen : ""}`}>
      <div className={styles.detailInner}>
        <div className={styles.card}>
          {isDyadLoading && dyadId ? (
            <div className={styles.loading}>
              <div className={styles.loadingBar} />
              <div className={styles.loadingBar} style={{ width: "60%" }} />
            </div>
          ) : (
            <>
              {/* ── Full analysis text first ── */}
              {children && (
                <div className={styles.analysisSection}>
                  <div className={styles.analysisContent}>{children}</div>
                </div>
              )}

              {/* ── Metadata row ── */}
              <div className={styles.metaStrip}>
                {statusScore && palette && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaKey}>Score</span>
                    <span className={styles.scoreDisplay}>
                      <span
                        className={styles.scoreDot}
                        style={{ backgroundColor: palette.bg }}
                      />
                      {statusScore}/5
                    </span>
                  </span>
                )}
                {trajectory && trajectory.toLowerCase() !== "neutral" && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaKey}>Trajectory</span>
                    <span
                      className={styles.trajectoryPill}
                      data-trajectory={trajectory.toLowerCase()}
                    >
                      {trajectory}
                    </span>
                  </span>
                )}
                {dyad?.latestAssessmentDate && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaKey}>Last assessed</span>
                    <span className={styles.metaValue}>
                      {new Date(dyad.latestAssessmentDate).toLocaleDateString(
                        "en-US",
                        { month: "short", day: "numeric", year: "numeric" },
                      )}
                    </span>
                  </span>
                )}
              </div>

              {/* ── Score history chart ── */}
              <ScoreHistoryChart
                data={scoreHistory ?? []}
                isLoading={isHistoryLoading}
                currentScore={statusScore}
              />

              {/* ── Company exposure ── */}
              {(dyad?.companyProfileContext || dyadId) && (
                <div className={styles.exposureSection}>
                  <div className={styles.exposureHeader}>
                    <p className={styles.sectionLabel}>
                      Company Exposure to this Dyad
                    </p>
                    {dyadId && !isEditing && (
                      <button
                        className={styles.editButton}
                        onClick={handleStartEdit}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {isEditing ? (
                    <div className={styles.editContainer}>
                      <textarea
                        className={styles.editTextarea}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        rows={3}
                        placeholder="Describe how the company is exposed to this bilateral relationship..."
                        maxLength={1000}
                      />
                      <div className={styles.editActions}>
                        <button
                          className={styles.cancelButton}
                          onClick={handleCancel}
                          disabled={updateMutation.isPending}
                        >
                          Cancel
                        </button>
                        <button
                          className={styles.saveButton}
                          onClick={handleSave}
                          disabled={updateMutation.isPending}
                        >
                          {updateMutation.isPending ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>
                  ) : dyad?.companyProfileContext ? (
                    <p className={styles.exposureText}>
                      {dyad.companyProfileContext}
                    </p>
                  ) : (
                    <p className={styles.exposurePlaceholder}>
                      No company exposure to this dyad set.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentDetailView;
